import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  BookOpen,
  Compass,
  Users,
  Sparkle,
  HandsPraying,
  Heart,
  Megaphone,
  UsersFour,
  Lock,
  ArrowRight,
} from "@phosphor-icons/react";

const ICONS = {
  BookOpen, Compass, Users, Sparkle, HandsPraying, Heart, Megaphone, UsersFour,
};

function ProgressRing({ progress = 0, size = 120, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, progress)) / 100) * c;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="progress-ring-bg" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="progress-ring-fill"
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
    </svg>
  );
}

export default function LearnPage() {
  const [foundations, setFoundations] = useState([]);
  const [paths, setPaths] = useState([]);
  const [progress, setProgress] = useState({ foundation_total: 0, foundation_completed: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/learn/paths"),
      api.get("/learn/progress"),
    ])
      .then(([pathsRes, progRes]) => {
        setFoundations(pathsRes.data.foundations || []);
        setPaths(pathsRes.data.paths || []);
        setProgress(progRes.data || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const foundationPct = progress.foundation_total
    ? Math.round((progress.foundation_completed / progress.foundation_total) * 100)
    : 0;

  return (
    <div className="px-5 md:px-8 py-4 max-w-4xl mx-auto" data-testid="learn-page">
      {/* Progress Card */}
      <div className="glass-card-strong p-6 mb-6 text-center relative overflow-hidden" data-testid="learn-progress">
        <div className="spiritual-orb-sm w-40 h-40 top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 animate-orb" />
        <div className="relative z-10">
          <h2 className="font-heading text-2xl text-[#2D1B0E] font-medium mb-1">Your Faith Journey</h2>
          <p className="text-[#8B7355] font-body text-sm mb-4">Foundations of Faith</p>
          <div className="flex justify-center mb-3">
            <ProgressRing progress={foundationPct} />
          </div>
          <p className="text-[#5C4033] font-body text-sm mb-4">
            {progress.foundation_completed || 0} / {progress.foundation_total || 0} lessons
          </p>
          {foundations[0] && (
            <Link
              to={`/learn/${foundations[0].slug}`}
              data-testid="continue-learning-btn"
              className="inline-block px-6 py-2.5 rounded-full glass-card text-[#5C4033] font-body text-sm font-medium hover:bg-white/60 transition-all"
            >
              Continue
            </Link>
          )}
        </div>
      </div>

      {/* Foundations */}
      <div className="mb-6">
        <h2 className="font-heading text-xl text-[#2D1B0E] font-medium mb-4">Start Your Foundation</h2>
        {loading ? (
          <div className="glass-card p-6 text-center text-[#8B7355] text-sm font-body">Loading foundations…</div>
        ) : (
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {foundations.map((f) => {
              const Icon = ICONS[f.icon] || BookOpen;
              const inner = (
                <div className={`glass-card p-5 min-w-[140px] text-center relative ${f.color} transition-all hover:-translate-y-0.5 ${f.is_locked ? "opacity-70" : ""}`}>
                  {f.is_locked && <Lock size={14} className="text-[#B8A088] absolute top-3 right-3" />}
                  <Icon size={28} weight="duotone" className="text-[#5C4033] mx-auto mb-2" />
                  <p className="font-body text-sm text-[#2D1B0E] font-medium">{f.name}</p>
                  <p className="font-body text-[10px] text-[#8B7355] mt-1">
                    {f.completed_lessons || 0} / {f.total_lessons || 0}
                  </p>
                </div>
              );
              return f.is_locked ? (
                <div key={f.id} data-testid={`foundation-${f.slug}`}>{inner}</div>
              ) : (
                <Link key={f.id} to={`/learn/${f.slug}`} data-testid={`foundation-${f.slug}`}>{inner}</Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Paths Grid */}
      <div className="mb-6">
        <h2 className="font-heading text-xl text-[#2D1B0E] font-medium mb-4">Paths</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {paths.map((p) => {
            const Icon = ICONS[p.icon] || BookOpen;
            return (
              <Link key={p.id} to={`/learn/${p.slug}`} data-testid={`path-${p.slug}`}>
                <div className={`glass-card p-4 text-center ${p.color} transition-all hover:-translate-y-0.5 cursor-pointer`}>
                  <Icon size={24} weight="duotone" className="text-[#5C4033] mx-auto mb-2" />
                  <p className="font-body text-xs text-[#2D1B0E] font-medium leading-tight">{p.name}</p>
                  {p.total_lessons > 0 && (
                    <p className="font-body text-[10px] text-[#8B7355] mt-1">
                      {p.completed_lessons || 0} / {p.total_lessons}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Go Deeper */}
      <div className="glass-card-strong p-5 text-center" data-testid="go-deeper">
        <h3 className="font-heading text-lg text-[#2D1B0E] font-medium mb-1">Go Deeper</h3>
        <p className="text-[#8B7355] font-body text-xs mb-4">Unlock structured discipleship and advanced teaching</p>
        <Link to="/give" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full glass-card text-[#5C4033] font-body text-sm hover:bg-white/60 transition-all">
          Learn More <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
